import React, { useState, useMemo } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Icon,
  Spinner,
  Center,
  Box,
} from '@chakra-ui/react';
import { MdEdit } from 'react-icons/md';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { useIMask } from 'react-imask';
import { useNavigate } from 'react-router-dom';

interface Usuario {
  seqUsuario: number;
  dscNome: string;
  dscCpf: string;
  datRegistro: string;
  sitRegistro: number;
  datInativo: string;
}

interface TabelaUsuarioProps {
  listaUsuario: Usuario[];
  listaVazia: string;
  loading: boolean;
}

type Ordenacao = { chave: keyof Usuario; direcao: 'asc' | 'desc' } | null;

const TabelaUsuario = ({ listaUsuario, listaVazia, loading }: TabelaUsuarioProps) => {
  const navigate = useNavigate();
  const [ordenacao, setOrdenacao] = useState<Ordenacao>(null);
  const [colunaDestacada, setColunaDestacada] = useState<keyof Usuario | null>(null);

  const { ref: cpfRef } = useIMask({ mask: '000.000.000-00' });

  function mascaraCpf(cpf: string) {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, '$1.$2.$3-$4');
  }

  const manipularOrdenacao = (chave: keyof Usuario) => {
    let direcao: 'asc' | 'desc' = 'asc';
    if (ordenacao && ordenacao.chave === chave && ordenacao.direcao === 'asc') {
      direcao = 'desc';
    }
    setOrdenacao({ chave, direcao });
  };

  const listaOrdenada = useMemo(() => {
    const copia = [...listaUsuario];
    if (ordenacao !== null) {
      copia.sort((a, b) => {
        const valorA = a[ordenacao.chave];
        const valorB = b[ordenacao.chave];
        if (typeof valorA === 'string' && typeof valorB === 'string') {
          const comparacao = valorA.localeCompare(valorB);
          return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
        }
        if (valorA < valorB) return ordenacao.direcao === 'asc' ? -1 : 1;
        if (valorA > valorB) return ordenacao.direcao === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return copia;
  }, [listaUsuario, ordenacao]);

  const obterIndicadorOrdenacao = (chave: keyof Usuario) => {
    if (ordenacao && ordenacao.chave === chave) {
      return ordenacao.direcao === 'asc' ? (
        <FaCaretUp color="#1789FC" style={{ marginLeft: '4px' }} />
      ) : (
        <FaCaretDown color="#1789FC" style={{ marginLeft: '4px' }} />
      );
    }
      return <FaCaretUp color="#4a5568" style={{ marginLeft: '4px' }} />;
  };

  const renderRows = () =>
    listaOrdenada.length > 0 ? (
      listaOrdenada.map((usuario) => (
        <Tr key={usuario.seqUsuario}>
          <Td>{mascaraCpf(usuario.dscCpf)}</Td>
          <Td>{usuario.dscNome}</Td>
          <Td>{usuario.sitRegistro === 1 ? 'Ativo' : 'Inativo'}</Td>
          <Td>{usuario.datRegistro.split('-').reverse().join('/')}</Td>
          <Td>{usuario.datInativo ? usuario.datInativo.split('-').reverse().join('/') : 'Não Inativado'}</Td>
          <Td>
            <IconButton
              aria-label="Botão Editar"
              bgColor="#fff"
              onClick={() => navigate(`editar-usuario/${usuario.seqUsuario}`)}
            >
              <Icon as={MdEdit} fontSize="24px" color="#1789FC" />
            </IconButton>
          </Td>
        </Tr>
      ))
    ) : (
      <Tr>
        <Td colSpan={6} color="#999898" textAlign="center">
          {listaVazia}
        </Td>
      </Tr>
    );

  return (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('dscCpf')}
              onMouseEnter={() => setColunaDestacada('dscCpf')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'dscCpf' ? 'blue.500' : 'inherit'}>
                CPF{obterIndicadorOrdenacao('dscCpf')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('dscNome')}
              onMouseEnter={() => setColunaDestacada('dscNome')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'dscNome' ? 'blue.500' : 'inherit'}>
                Nome{obterIndicadorOrdenacao('dscNome')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('sitRegistro')}
              onMouseEnter={() => setColunaDestacada('sitRegistro')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'sitRegistro' ? 'blue.500' : 'inherit'}>
                Situação{obterIndicadorOrdenacao('sitRegistro')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('datRegistro')}
              onMouseEnter={() => setColunaDestacada('datRegistro')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'datRegistro' ? 'blue.500' : 'inherit'}>
                Data do Registro{obterIndicadorOrdenacao('datRegistro')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('datInativo')}
              onMouseEnter={() => setColunaDestacada('datInativo')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'datInativo' ? 'blue.500' : 'inherit'}>
                Data de Inativação{obterIndicadorOrdenacao('datInativo')}
              </Box>
            </Th>
            <Th>Editar</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <Tr>
              <Td colSpan={6}>
                <Center>
                  <Spinner size="lg" color="blue.500" />
                </Center>
              </Td>
            </Tr>
          ) : (
            renderRows()
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default TabelaUsuario;
